import { useEffect } from "react";
import {
  EVENT_CTA_CLICK,
  EVENT_EMAIL_CLICK,
  EVENT_LINK_CLICK,
  EVENT_OUTBOUND_LINK_CLICK,
  EVENT_PHONE_CLICK,
  pushEvent,
} from "@/lib/gtm-events";

type LinkType = "internal" | "external" | "anchor" | "mailto" | "tel" | "other";

function classify(href: string, hostname: string): LinkType {
  if (!href) return "other";
  if (href.startsWith("mailto:")) return "mailto";
  if (href.startsWith("tel:")) return "tel";
  if (href.startsWith("#")) return "anchor";
  try {
    const url = new URL(href, window.location.href);
    if (url.protocol !== "http:" && url.protocol !== "https:") return "other";
    return url.hostname === hostname ? "internal" : "external";
  } catch {
    return "other";
  }
}

/**
 * Global `<a>` click delegation → dataLayer.
 *
 * Pushes `link_click` for every anchor click (internal, external, anchor,
 * mailto, tel) and an extra `outbound_link_click` for external URLs so GTM
 * doesn't need to rely on its own built-in Link Click trigger — which is
 * unreliable in an SPA because TanStack Router intercepts navigation before
 * GTM sees the full document unload.
 *
 * Parameters:
 *  - link_url    Full resolved URL (or raw href for mailto/tel/anchor)
 *  - link_domain Hostname for http(s) URLs
 *  - link_type   internal | external | anchor | mailto | tel | other
 *  - link_text   Trimmed visible text (max 120 chars)
 *  - nav_item    `data-nav-item` attribute or aria-label, if set
 *  - page_path   Target pathname for internal links
 *  - from_path   Current pathname when the click happened
 */
export function LinkClickTracker() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const onClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const control = target.closest("a,button,[data-track]");
      const location = control?.closest("header,nav") ? "header" : control?.closest("footer") ? "footer" : control?.closest("form") ? "form" : "main";
      if (control && (control.hasAttribute("data-track") || control.classList.contains("btn-cta") || control.matches('button[type="submit"]'))) {
        const controls = [...document.querySelectorAll("a.btn-cta,button[type=submit],[data-track]")];
        const label = control.getAttribute("data-track") || `cta-${controls.indexOf(control)}`;
        let targetPath: string | undefined;
        try { targetPath = new URL(control.getAttribute("href") ?? "", window.location.href).pathname; } catch { /* no target */ }
        pushEvent(EVENT_CTA_CLICK, { label, location, target: targetPath });
      }
      const anchor = target.closest("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href) return;

      const type = classify(href, window.location.hostname);
      const label = anchor.getAttribute("data-track") || (type === "tel" ? "phone" : type === "mailto" ? "email" : "cta");
      const text = label;
      const navItem =
        anchor.getAttribute("data-nav-item") ||
        anchor.getAttribute("aria-label") ||
        undefined;

      let linkUrl = href;
      let linkDomain: string | undefined;
      let pagePath: string | undefined;
      if (type === "internal" || type === "external") {
        try {
          const url = new URL(href, window.location.href);
          linkUrl = url.href;
          linkDomain = url.hostname;
          if (type === "internal") pagePath = url.pathname;
        } catch {
          // keep raw href
        }
      }

      const params = {
        link_url: linkUrl,
        link_domain: linkDomain,
        link_type: type,
        link_text: text,
        nav_item: navItem,
        page_path: pagePath,
        from_path: window.location.pathname,
      };

      pushEvent(EVENT_LINK_CLICK, params);
      // Primaire CTA-knoppen (btn-cta) krijgen een eigen event, zodat in GA4
      // zichtbaar is welke boodschap op welke pagina converteert.
      if (type === "external") {
        pushEvent(EVENT_OUTBOUND_LINK_CLICK, params);
      } else if (type === "tel") {
        pushEvent(EVENT_PHONE_CLICK, { label: "phone", location });
      } else if (type === "mailto") {
        pushEvent(EVENT_EMAIL_CLICK, { label: "email", location });
      }
    };

    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  return null;
}